/**
 * <AdminPulseItems /> — admin panel to manage all Neighborhood Pulse posts.
 *
 * Table view sorted by date (newest first). Search, filter by status +
 * featured. Row actions: toggle Feature, Archive/Restore, Edit, Delete.
 * Edit opens the same <PulseItemForm> used for creation.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Star, StarOff, Archive, ArchiveRestore, Pencil, Trash2, Search,
  ExternalLink, Loader2, RadioTower, Filter,
} from "lucide-react";
import PulseItemForm from "@/components/PulseItemForm";

const STATUS_OPTIONS = [
  { v: "",         label: "All statuses" },
  { v: "active",   label: "Active" },
  { v: "archived", label: "Archived" },
  { v: "hidden",   label: "Hidden" },
];

const CATEGORY_META = {
  community: { label: "Community", emoji: "🤝", color: "#0891B2" },
  event:     { label: "Event",     emoji: "📅", color: "#B25D38" },
  news:      { label: "News",      emoji: "📰", color: "#7C3AED" },
  info:      { label: "Info",      emoji: "ℹ️", color: "#059669" },
};

const PAGE_SIZE = 25;

export default function AdminPulseItems() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ active: 0, archived: 0, hidden: 0, featured: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [featured, setFeatured] = useState("");
  const [category, setCategory] = useState("");
  const [skip, setSkip] = useState(0);

  // editor modal
  const [editing, setEditing] = useState(null); // pulse-item object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (featured) params.featured = featured === "yes";
      if (category) params.category = category;
      const { data } = await http.get("/admin/pulse-items", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || stats);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load posts.");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, featured, category, skip]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, updates, successMsg) => {
    setBusyId(id);
    try {
      const { data } = await http.patch(`/admin/pulse-items/${id}`, updates);
      setItems((rows) => rows.map((r) => r.id === id ? data : r));
      toast.success(successMsg);
      // Refresh stats if status/featured changed.
      if ("status" in updates || "is_featured" in updates) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally { setBusyId(null); }
  };

  const toggleFeature = (item) => patch(item.id, { is_featured: !item.is_featured }, item.is_featured ? "Unfeatured." : "Featured on homepage.");
  const setCategoryFor = (item, next) => patch(item.id, { category: next }, `Categorised as ${next}.`);
  const archive     = (item) => patch(item.id, { status: "archived" }, "Archived.");
  const restore     = (item) => patch(item.id, { status: "active" }, "Restored.");
  const hide        = (item) => patch(item.id, { status: "hidden" }, "Hidden.");

  const remove = async (item) => {
    if (!confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    setBusyId(item.id);
    try {
      await http.delete(`/admin/pulse-items/${item.id}`);
      setItems((rows) => rows.filter((r) => r.id !== item.id));
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-pulse-items">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl">Neighborhood Pulse — all posts</h2>
        <div className="text-xs opacity-70">
          <span data-testid="admin-pulse-count-total">{total}</span> total ·{" "}
          <span className="text-emerald-700" data-testid="admin-pulse-count-active">{stats.active}</span> active ·{" "}
          <span style={{ color: "var(--wofy-primary)" }} data-testid="admin-pulse-count-featured">{stats.featured}</span> featured ·{" "}
          <span className="opacity-60" data-testid="admin-pulse-count-archived">{stats.archived}</span> archived
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 opacity-50" />
          <input
            className="wofy-input pl-9 w-full"
            placeholder="Search title, description, author, city…"
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            data-testid="admin-pulse-search"
          />
        </div>
        <select className="wofy-input" value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }} data-testid="admin-pulse-filter-status">
          {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select className="wofy-input" value={featured} onChange={(e) => { setSkip(0); setFeatured(e.target.value); }} data-testid="admin-pulse-filter-featured">
          <option value="">All (featured & not)</option>
          <option value="yes">Featured only</option>
          <option value="no">Not featured</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-14"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-14 opacity-70" data-testid="admin-pulse-empty">No posts match your filters.</div>
      ) : (
        <div className="overflow-x-auto border rounded-xl" style={{ borderColor: "var(--wofy-border)" }}>
          <table className="w-full text-sm" data-testid="admin-pulse-table">
            <thead className="bg-black/[0.03]">
              <tr className="text-left">
                <th className="p-3 font-mono uppercase tracking-wider text-[10px]">Date</th>
                <th className="p-3 font-mono uppercase tracking-wider text-[10px]">Title & author</th>
                <th className="p-3 font-mono uppercase tracking-wider text-[10px]">Location</th>
                <th className="p-3 font-mono uppercase tracking-wider text-[10px]">Status</th>
                <th className="p-3 font-mono uppercase tracking-wider text-[10px] text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t align-top" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-pulse-row-${it.id}`}>
                  <td className="p-3 font-mono text-[11px] whitespace-nowrap">{new Date(it.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div className="flex items-start gap-2">
                      {it.is_featured && <Star className="w-4 h-4 mt-0.5" style={{ color: "var(--wofy-primary)", fill: "var(--wofy-primary)" }} />}
                      <div className="flex-1 min-w-0">
                        <Link to={`/pulse-item/${it.id}`} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline inline-flex items-center gap-1" data-testid={`admin-pulse-view-${it.id}`}>
                          {it.title} <ExternalLink className="w-3 h-3 opacity-60" />
                        </Link>
                        {it.description && <div className="text-xs opacity-70 line-clamp-2">{it.description}</div>}
                        <div className="text-[10px] mt-1 opacity-60">by {it.user_name || "—"} {it.user_handle ? `(@${it.user_handle})` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{[it.city, it.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full ${it.status === "active" ? "bg-emerald-100 text-emerald-800" : it.status === "archived" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
                      {it.status || "active"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => toggleFeature(it)}
                        disabled={busyId === it.id}
                        className="p-1.5 rounded-md hover:bg-black/[0.06]"
                        title={it.is_featured ? "Remove from homepage" : "Feature on homepage"}
                        data-testid={`admin-pulse-feature-${it.id}`}
                      >
                        {it.is_featured
                          ? <StarOff className="w-4 h-4" />
                          : <Star className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(it)}
                        disabled={busyId === it.id}
                        className="p-1.5 rounded-md hover:bg-black/[0.06]"
                        title="Edit"
                        data-testid={`admin-pulse-edit-${it.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {it.status === "archived" ? (
                        <button
                          type="button"
                          onClick={() => restore(it)}
                          disabled={busyId === it.id}
                          className="p-1.5 rounded-md hover:bg-black/[0.06]"
                          title="Restore to active"
                          data-testid={`admin-pulse-restore-${it.id}`}
                        >
                          <ArchiveRestore className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => archive(it)}
                          disabled={busyId === it.id}
                          className="p-1.5 rounded-md hover:bg-black/[0.06]"
                          title="Archive"
                          data-testid={`admin-pulse-archive-${it.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(it)}
                        disabled={busyId === it.id}
                        className="p-1.5 rounded-md hover:bg-red-100 text-red-600"
                        title="Delete"
                        data-testid={`admin-pulse-delete-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="opacity-70">Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0}
              className="wofy-btn-ghost text-xs"
              data-testid="admin-pulse-prev"
            >← Prev</button>
            <button
              type="button"
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + items.length >= total}
              className="wofy-btn-ghost text-xs"
              data-testid="admin-pulse-next"
            >Next →</button>
          </div>
        </div>
      )}

      {/* Edit modal — reuses the same PulseItemForm as the public "Add a post" */}
      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setEditing(null)} data-testid="admin-pulse-edit-modal">
          <div className="wofy-card max-w-2xl w-full max-h-[90vh] overflow-y-auto !p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl inline-flex items-center gap-2">
                <RadioTower className="w-4 h-4" /> Edit pulse item
              </h3>
              <button onClick={() => setEditing(null)} className="opacity-60 hover:opacity-100" data-testid="admin-pulse-edit-close">✕</button>
            </div>
            <PulseItemForm
              editing={editing}
              onSaved={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
